import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AnimalMap from '../components/AnimalMap';
import './TemporaryIntersectionPage.css';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const PREVIEW_KEY = 'faunatrack-temporary-intersection';

function getSavedAnimalIds() {
  try {
    const ids = JSON.parse(sessionStorage.getItem(PREVIEW_KEY) || '[]');
    if (!Array.isArray(ids) || ids.length > 50) return [];
    return [...new Set(ids.filter(id =>
      Number.isSafeInteger(id) && id > 0
    ))];
  } catch {
    return [];
  }
}

function getImageUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_URL}/${path.replace(/^\/+/, '')}`;
}

function TemporaryIntersectionPage() {
  const navigate = useNavigate();
  const [animalIds, setAnimalIds] = useState(getSavedAnimalIds);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (animalIds.length === 0) {
      setResult(null);
      setError('');
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    async function calculate() {
      setLoading(true);
      setError('');
      setResult(null);

      try {
        const response = await fetch(
          `${API_URL}/animals/preview-intersection`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ animal_ids: animalIds }),
            signal: controller.signal
          }
        );
        const data = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(data?.error || 'Unable to calculate intersection.');
        }
        if (!data || !Array.isArray(data.animals)) {
          throw new Error('Invalid response from the server.');
        }

        if (!controller.signal.aborted) setResult(data);
      } catch (err) {
        if (err.name !== 'AbortError' && !controller.signal.aborted) {
          setError(err.message);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    calculate();
    return () => controller.abort();
  }, [animalIds]);

  function updateSelection(nextIds) {
    setAnimalIds(nextIds);
    try {
      if (nextIds.length) {
        sessionStorage.setItem(PREVIEW_KEY, JSON.stringify(nextIds));
      } else {
        sessionStorage.removeItem(PREVIEW_KEY);
      }
    } catch {
      // Keep the selection in memory if storage is unavailable.
    }
  }

  function removeAnimal(id) {
    updateSelection(animalIds.filter(animalId => animalId !== id));
  }

  return (
    <div className="temporary-intersection">
      <div className="temporary-intersection__header">
        <div>
          <h1>Temporary Animal Intersection</h1>
          <p>
            Compare the geographic ranges of selected animals without
            creating an account or saving a list.
          </p>
        </div>
        <button
          type="button"
          className="temporary-intersection__back"
          onClick={() => navigate('/')}
        >
          Browse animals
        </button>
      </div>

      {animalIds.length === 0 ? (
        <div className="temporary-intersection__empty">
          <h2>No animals selected</h2>
          <p>
            Go to <Link to="/">Home</Link> or{' '}
            <Link to="/explore">Explore</Link>, choose
            <strong> Select Many</strong>, select at least two animals,
            and click <strong>Check Intersection</strong>.
          </p>
        </div>
      ) : (
        <>
          <div className="temporary-intersection__section-head">
            <h2>Selected animals ({animalIds.length})</h2>
            <button
              type="button"
              onClick={() => updateSelection([])}
              className="temporary-intersection__clear"
            >
              Clear all
            </button>
          </div>

          {loading && <p role="status">Calculating geographic overlap...</p>}
          {error && (
            <p role="alert" className="temporary-intersection__error">
              {error}
            </p>
          )}

          {result && (
            <>
              <div className="temporary-intersection__grid">
                {result.animals.map(animal => {
                  const imageUrl = getImageUrl(animal.image_path);
                  return (
                    <article
                      className="temporary-intersection__animal"
                      key={animal.id}
                    >
                      <Link
                        to={`/animal/${animal.id}`}
                        className="temporary-intersection__animal-link"
                      >
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={animal.name || animal.sci_name}
                            loading="lazy"
                          />
                        ) : (
                          <div className="temporary-intersection__placeholder">
                            No image available
                          </div>
                        )}
                        <strong>
                          {animal.name || animal.sci_name || 'Unnamed animal'}
                        </strong>
                        {animal.name && animal.sci_name && (
                          <small>{animal.sci_name}</small>
                        )}
                      </Link>
                      <button
                        type="button"
                        className="temporary-intersection__remove"
                        onClick={() => removeAnimal(animal.id)}
                        aria-label={`Remove ${animal.name || animal.sci_name}`}
                      >
                        Remove
                      </button>
                    </article>
                  );
                })}
              </div>

              <section className="temporary-intersection__map-section">
                <h2>
                  {animalIds.length === 1
                    ? 'Geographic Range'
                    : 'Geographic Intersection Map'}
                </h2>
                {result.intersectionStatus === 'missing_geometry' ? (
                  <p className="temporary-intersection__error">
                    One or more selected animals do not have a geographic
                    range in the database. Remove those animals to
                    calculate the intersection.
                  </p>
                ) : result.geometry ? (
                  <div className="temporary-intersection__map">
                    <AnimalMap geojson={result.geometry} />
                  </div>
                ) : (
                  <p className="temporary-intersection__no-overlap">
                    These animals have no shared geographic area.
                  </p>
                )}
              </section>
              <p className="temporary-intersection__hint">
                Removing an animal automatically recalculates the map.
                This collection is temporary and is not saved to an account.
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default TemporaryIntersectionPage;
